import React from "react";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";

import classNames from "classnames";

import { useIndexContext } from "../contexts/Index";
import useTableHandlers from "../hooks/useTableHandlers";

import { selectSetting } from "../../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../../hooks/reduxHooks";
import useUserSwitcher from "../../../../../hooks/useUserSwitcher";
import { AUTH_LEVELS, TEAM_LOGO_URL } from "../../../../../lib/globals";

const ClubhouseTable = (): React.JSX.Element => {
    const { handleUserSwitch } = useUserSwitcher();
    const { user } = useAppSelector(state => state.auth);
    const { handleHeaderClick } = useTableHandlers();
    const { sortColumn, sortOrder, teams, filters } = useIndexContext();

    const currentYear = parseInt(
        useAppSelector(state =>
            selectSetting(state, "majorybl_current_league_year"),
        )?.setting,
    );

    return (
        <>
            <Table
                data-testid="clubhouseTable"
                className="clubhouseTable"
                striped
                bordered
                responsive
            >
                <thead>
                    <tr>
                        <th data-sortable="false">Logo</th>
                        <th
                            onClick={() => handleHeaderClick("team")}
                            className={classNames({
                                sorting_asc:
                                    sortColumn === "team" &&
                                    sortOrder === "ascending",
                                sorting_desc:
                                    sortColumn === "team" &&
                                    sortOrder === "descending",
                            })}
                        >
                            Team
                        </th>
                        <th
                            onClick={() => handleHeaderClick("age")}
                            className={classNames({
                                sorting_asc:
                                    sortColumn === "age" &&
                                    sortOrder === "ascending",
                                sorting_desc:
                                    sortColumn === "age" &&
                                    sortOrder === "descending",
                            })}
                        >
                            Age Level
                        </th>
                        <th
                            onClick={() => handleHeaderClick("firstName")}
                            className={classNames({
                                sorting_asc:
                                    sortColumn === "firstName" &&
                                    sortOrder === "ascending",
                                sorting_desc:
                                    sortColumn === "firstName" &&
                                    sortOrder === "descending",
                            })}
                        >
                            First Name
                        </th>
                        <th
                            onClick={() => handleHeaderClick("lastName")}
                            className={classNames({
                                sorting_asc:
                                    sortColumn === "lastName" &&
                                    sortOrder === "ascending",
                                sorting_desc:
                                    sortColumn === "lastName" &&
                                    sortOrder === "descending",
                            })}
                        >
                            Last Name
                        </th>
                        <th data-sortable="false">Phone</th>
                        <th data-sortable="false">Email</th>
                        {user &&
                            AUTH_LEVELS.leaguemanagement.includes(
                                user.userLevel,
                            ) && <th data-sortable="false"></th>}
                    </tr>
                </thead>
                <tbody>
                    {teams
                        .filter(t =>
                            filters.search
                                ? t.name
                                      .toLowerCase()
                                      .includes(filters.search.toLowerCase()) ||
                                  t.coach.email
                                      .toLowerCase()
                                      .includes(filters.search.toLowerCase()) ||
                                  t.coach.firstName
                                      .toLowerCase()
                                      .includes(filters.search.toLowerCase()) ||
                                  t.coach.lastName
                                      .toLowerCase()
                                      .includes(filters.search.toLowerCase()) ||
                                  t.coach.phone
                                      .toLowerCase()
                                      .includes(filters.search.toLowerCase())
                                : true,
                        )
                        .filter(t =>
                            user &&
                            AUTH_LEVELS.leaguemanagement.includes(
                                user.userLevel,
                            )
                                ? filters.activeLeague ===
                                  t.currentAgeLevel.season.season
                                : true,
                        )
                        .filter(t =>
                            filters.ageLevels.filter && t.currentAgeLevel
                                ? filters.ageLevels.filter.includes(
                                      t.currentAgeLevel?.ageLevel,
                                  )
                                : true,
                        )
                        .filter(t =>
                            filters.leagues.filter
                                ? t.leagues
                                    ? t.leagues.some(
                                          l =>
                                              l.year === currentYear &&
                                              filters.leagues.filter?.includes(
                                                  l.name,
                                              ),
                                      )
                                    : false
                                : true,
                        )
                        .map(t => {
                            const currentTeamsLeague = t?.leagues?.find(
                                l => l.year === currentYear,
                            );

                            return (
                                <tr key={`${currentTeamsLeague?.id}-${t.id}`}>
                                    <td>
                                        {t.logo && (
                                            <Link
                                                to={`/coachs-clubhouse/team/${t.id}`}
                                            >
                                                <img
                                                    src={`${TEAM_LOGO_URL}${t.logo}`}
                                                    alt={`${t.name} logo`}
                                                    className="teamLogo img-fluid"
                                                />
                                            </Link>
                                        )}
                                    </td>
                                    <td>
                                        <h5>
                                            <Link
                                                to={`/coachs-clubhouse/team/${t.id}`}
                                            >
                                                {t.name}
                                            </Link>
                                        </h5>
                                        {currentTeamsLeague?.name ||
                                            t.currentAgeLevel?.ageLevel}
                                    </td>
                                    <td>{t.currentAgeLevel?.ageLevel}</td>
                                    <td>{t.coach.firstName}</td>
                                    <td>{t.coach.lastName}</td>
                                    <td>{t.coach.phone}</td>
                                    <td>
                                        <a href={`mailto:${t.coach.email}`}>
                                            {t.coach.email}
                                        </a>
                                    </td>
                                    {user &&
                                        AUTH_LEVELS.leaguemanagement.includes(
                                            user.userLevel,
                                        ) && (
                                            <td>
                                                <span
                                                    className={classNames(
                                                        "link",
                                                    )}
                                                    onClick={() =>
                                                        handleUserSwitch(
                                                            t.coach.id,
                                                        )
                                                    }
                                                >
                                                    switch to user
                                                </span>
                                            </td>
                                        )}
                                </tr>
                            );
                        })}
                </tbody>
            </Table>
        </>
    );
};

export default ClubhouseTable;
